import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TagService } from '@sb-tools/typescript/services/tag.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BACKSPACE, DOWN_ARROW, UP_ARROW } from '@sb-tools/typescript/constants/keycodes';
import { Tag } from '@sb-tools/typescript/models/tag.model';
import { TicketListService } from '@form-field-module/common/services/ticket-list.service';
import { InlineSVGComponent } from '../../Frontend-Inline-SVG-Module/inline-svg.component';
import { UserAvatarComponent } from '../../Frontend-User-Avatar-Module/user-avatar.component';
import { NgClass } from '@angular/common';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { FormsModule } from '@angular/forms';
import { FocusDirective } from '@sb-tools/typescript/directives/focus.directive';
import { ReplaceEmojisPipe } from '@sb-tools/typescript/pipes/replace-emoji/replace-emojis.pipe';
import { TruncatePipe } from '@sb-tools/typescript/pipes/truncate.pipe';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-tags-input',
    templateUrl: './tags-input.component.html',
    styleUrls: ['./tags-input.component.scss'],
    imports: [
        InlineSVGComponent,
        UserAvatarComponent,
        NgClass,
        TypeaheadModule,
        FormsModule,
        FocusDirective,
        ReplaceEmojisPipe,
        TruncatePipe,
        TranslateModule,
    ],
})
export class TagsInputComponent implements OnInit, OnDestroy, OnChanges {
    @ViewChild('typeaheadInput') typeaheadInput;

    @Input() addingValidator?: (email: string) => boolean;
    @Input() addOnBlur?: boolean;
    @Input() autofocus = false;
    @Input() contentType?: string = 'tags';
    @Input() displayBy: string;
    @Input() focusTimeout = 0;
    @Input() identifyBy?: string;
    @Input() isAdaptivePosition?: boolean = true;
    @Input() isContainerBody?: boolean = true;
    @Input() isNotFromListAllowed?: boolean;
    @Input() isInvalid?: boolean;
    @Input() isTagCreationEnabled?: boolean = false;
    @Input() items = [];
    @Input() maxItems?: number;
    @Input() noResultMessage?: string = '';
    @Input() placeholder?: string = '';
    @Input() replyId?: string = '';
    @Input() typeaheadMinLength?: number;
    @Input() selectedItems: any = [];
    @Input() separatorKeys?: number[];
    @Input() typeaheadGroupField = '';

    @Output() add: EventEmitter<any> = new EventEmitter<any>();
    @Output() delete: EventEmitter<any> = new EventEmitter<any>();

    clonedItems = [];
    isItemNotFound = false;
    isItemNotMatched = false;
    selectedText: string;
    typeaheadGroupFieldDefaultValue: string;
    typeaheadOptionsInScrollableView;
    typeaheadOptionsLimit = 50;

    private unsubscriber = new Subject<void>();

    constructor(
        private tagService: TagService,
        private ticketListService: TicketListService,
    ) {}

    ngOnInit(): void {
        if (!this.typeaheadMinLength) {
            this.typeaheadMinLength = 0;
        }

        if (!this.identifyBy) {
            this.identifyBy = 'id';
        }

        this.typeaheadGroupFieldDefaultValue = this.typeaheadGroupField;
        this.typeaheadOptionsInScrollableView = this.contentType === 'tags' ? 10 : 5;
    }

    ngOnChanges(): void {
        this.clonedItems = structuredClone(this.items);
        if (this.contentType === 'users' && this.selectedItems.length) {
            this.selectedItems.forEach((selectedItem) => {
                const selectedItemIndex = this.clonedItems.findIndex((item) => {
                    return item.id === selectedItem.id;
                });

                if (selectedItemIndex > -1) {
                    this.clonedItems[selectedItemIndex].isSelected = true;
                }
            });
        }
    }

    createTag(): void {
        this.clonedItems = this.clonedItems.filter((item) => {
            return !item.isFake;
        });

        const newTag: Tag = {
            text: this.selectedText.trim(),
            autoAssignment: false,
            isGlobal: true,
            communityId: null,
            id: null,
        };

        this.tagService
            .add(newTag)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.tagService.addGlobalTag(data);

                this.tagService.availableTagsForTicket = this.tagService.tagsByCategory.concat(
                    this.tagService.globalTags,
                );

                this.clonedItems = this.tagService.availableTagsForTicket;

                this.onSelect({ item: data });
                this.isItemNotFound = false;
            });
    }

    keyDownHandler(event): boolean {
        if (event.which === UP_ARROW || event.which === DOWN_ARROW) {
            return;
        }

        if (this.isNotFromListAllowed && this.separatorKeys) {
            if (this.separatorKeys.includes(event.keyCode)) {
                this.addNewItem();
                return false;
            }
        }

        if (event && event.keyCode === BACKSPACE && !event.target.value) {
            if (this.selectedItems.length) {
                this.markItemAsSelected(this.selectedItems.length - 1);
                this.selectedItems.pop();
            }

            $('typeahead-container').removeClass('disable-option');
        } else {
            setTimeout(() => {
                this.clonedItems = this.clonedItems.filter((item) => {
                    return !item.isFake;
                });

                const val = this.typeaheadInput.nativeElement.value;

                if (val) {
                    const valLower = val.toLowerCase();

                    this.isItemNotMatched = !this.clonedItems.find((item) => {
                        return item[this.displayBy].toLowerCase().includes(valLower);
                    });

                    if (this.isItemNotMatched) {
                        this.typeaheadGroupField = '';
                        this.isItemNotFound = true;
                    } else {
                        this.typeaheadGroupField = this.typeaheadGroupFieldDefaultValue;
                        this.isItemNotFound = !this.clonedItems.find((item) => {
                            return item[this.displayBy].toLowerCase() === valLower;
                        });
                    }

                    if (this.isItemNotMatched || this.isItemNotFound) {
                        const fakeItem = {
                            isFake: true,
                            [this.typeaheadGroupField]: '',
                        };

                        fakeItem[this.identifyBy] = val;
                        fakeItem[this.displayBy] = val;
                        this.clonedItems.push(fakeItem);
                    }
                } else {
                    this.typeaheadGroupField = this.typeaheadGroupFieldDefaultValue;
                }

                if (!(this.isTagCreationEnabled && this.isItemNotFound)) {
                    $('typeahead-container').addClass('disable-option');
                } else {
                    $('typeahead-container').removeClass('disable-option');
                }
            }, 1);
        }
        return true;
    }

    onSelect(event: any): void {
        if (event.item.isFake) {
            if (this.isTagCreationEnabled && this.isItemNotFound && this.selectedText && this.selectedText.trim()) {
                if (this.contentType === 'tags') {
                    this.createTag();
                } else {
                    this.addNewItem();
                }
            }

            return;
        }

        if (
            !this.selectedItems.find((selectedItem) => {
                return (
                    selectedItem[this.identifyBy].toString().toLowerCase() ===
                    event.item[this.identifyBy].toString().toLowerCase()
                );
            })
        ) {
            const index = this.clonedItems.findIndex((selectedItem) => {
                return selectedItem.id === event.item.id;
            });
            if (index > -1) {
                this.clonedItems[index].isSelected = true;
            }
            this.selectedItems.push(event.item);
            this.add.emit(event.item);
        }

        this.selectedText = '';
    }

    removeItem(index: number): void {
        this.markItemAsSelected(index);
        this.selectedItems.splice(index, 1);
    }

    typeaheadOnBlur(): void {
        if (this.isNotFromListAllowed) {
            this.addNewItem();
        }
    }

    typeaheadOnFocus(): void {
        this.ticketListService.isTicketListActive = false;
    }

    private addNewItem(): void {
        if (this.addingValidator(this.selectedText)) {
            const newEvent = {
                item: {},
            };
            newEvent.item[this.identifyBy] = this.selectedText;
            newEvent.item[this.displayBy] = this.selectedText;
            this.onSelect(newEvent);
        }
    }

    private markItemAsSelected(index): void {
        const selectedItemIndex = this.clonedItems.findIndex((selectedItem) => {
            return selectedItem[this.identifyBy] === this.selectedItems[index][this.identifyBy];
        });
        if (selectedItemIndex > -1) {
            this.clonedItems[selectedItemIndex].isSelected = false;
        }

        this.delete.emit(index);
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
